<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <!-- Navigation and Tool Header -->
    <div class="mb-8">
      <div class="flex items-center mb-6">
        <a :href="toolsUrl" class="text-blue-500 hover:text-blue-700 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>Back to Tools</span>
        </a>
      </div>

      <!-- Tool Header Card -->
      <div v-if="tool" class="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-6">
        <div class="p-6 md:p-8">
          <div class="flex flex-col md:flex-row items-center md:items-start gap-6">
            <!-- Tool Icon and Category -->
            <div v-if="tool.icon && tool.category_color" class="flex-shrink-0">
              <div class="w-16 h-16 rounded-full flex items-center justify-center shadow-sm" :class="`bg-${tool.category_color}-100`">
                <i :class="`${tool.icon} text-${tool.category_color}-500 text-2xl`"></i>
              </div>
            </div>
            
            <!-- Tool Name and Description -->
            <div class="flex-grow text-center md:text-left">
              <div class="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                <h1 class="text-2xl md:text-3xl font-bold tracking-tight">{{ tool.name }}</h1>
                <span v-if="tool.category" class="inline-block px-3 py-1 rounded-full text-xs font-medium" 
                  :class="`bg-${tool.category_color}-100 text-${tool.category_color}-700`">
                  {{ tool.category }}
                </span>
              </div>
              <p class="text-gray-600">{{ tool.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="tool" class="flex flex-col space-y-8">
      <!-- Main Content Area -->
      <div class="flex flex-col lg:flex-row gap-6">
        <!-- === INPUT PANEL === -->
        <!-- Only show when in input mode or when no content has been generated -->
        <div v-if="currentView === 'input' || !generatedContent" 
          class="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 flex-1">
          <div class="p-6">
            <!-- Main Form -->
            <form @submit.prevent="generateContent">
              <!-- Prompt Details (if required) -->
<!--              <div v-if="tool.prompt_required" class="mb-5">-->
<!--                <label :for="'prompt-details'" class="block text-xl font-medium tracking-tight text-gray-700 mb-2">-->
<!--                  {{ tool.prompt_label || customLabels.promptDetails || 'What would you like to create?' }}-->
<!--                </label>-->
<!--                <textarea -->
<!--                  id="prompt-details"-->
<!--                  v-model="formData.prompt_details"-->
<!--                  rows="5"-->
<!--                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"-->
<!--                  required-->
<!--                  :placeholder="tool.input_placeholder || 'Describe what you want to generate...'"-->
<!--                ></textarea>-->
<!--              </div>-->

              <voice-text-area v-if="tool.prompt_required" :model-object="formData" :field-name="'prompt_details'" :text-label="tool.prompt_label"
                :text-placeholder="tool.input_placeholder || 'Describe what you want to generate...'"
                :text-voice-help-text="''" ></voice-text-area>
              
              <p v-if="tool.prompt_required && tool.input_instructions" class="mt-1 text-sm text-gray-400">
                {{ tool.input_instructions }}
              </p>

              <!-- Input (if required) -->
              <div v-if="tool.input_required" class="mb-5">
                <div class="flex justify-between items-center mb-2">
                  <label :for="'input-text'" class="block text-sm font-medium text-gray-700">
                    {{ tool.input_label }}
                  </label>
                  
                  <!-- Example toggle button - only if example exists -->
                  <button 
                    v-if="tool.input_example"
                    type="button" 
                    @click="toggleExample"
                    class="text-sm text-blue-500 hover:text-blue-700 flex items-center"
                  >
                    <span v-if="!showExample">See example</span>
                    <span v-else>Hide example</span>
                  </button>
                </div>
                
                <!-- Example display -->
                <div v-if="showExample && tool.input_example" class="mb-3 p-3 bg-blue-50 rounded border border-blue-100 text-sm">
                  <div class="whitespace-pre-wrap text-gray-700">{{ tool.input_example }}</div>
                  <button 
                    type="button" 
                    @click="useExample" 
                    class="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Use this example
                  </button>
                </div>
                
                <textarea 
                  id="input-text"
                  v-model="formData.input"
                  rows="15"
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  required
                  :placeholder="tool.input_placeholder || `Enter your ${tool.input_label.toLowerCase()}`"
                ></textarea>
              </div>
              
              <!-- Tone Selection (if required) -->
              <div v-if="tool.tone_required" class="mb-5">
                <label :for="'tone'" class="block text-sm font-medium text-gray-700 mb-2">
                  Tone
                </label>
                <select 
                  id="tone"
                  v-model="formData.tone"
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  required
                >
                  <option value="Professional">Professional</option>
                  <option value="Casual">Casual</option>
                  <option value="Friendly">Friendly</option>
                  <option value="Formal">Formal</option>
                  <option value="Enthusiastic">Enthusiastic</option>
                  <option value="Informative">Informative</option>
                </select>
              </div>
              
              <!-- Generate Button -->
              <div class="mt-6">
                <button 
                  type="submit" 
                  class="py-3 px-4 rounded-lg text-white font-medium relative overflow-hidden transition-all"
                  :class="tool.category_color ? `bg-${tool.category_color}-500 hover:bg-${tool.category_color}-600` : 'bg-blue-500 hover:bg-blue-600'"
                  :disabled="isGenerating"
                >
                  <span class="flex items-center justify-center">
                    <svg v-if="isGenerating" class="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ isGenerating ? 'Generating...' : (tool.action_label || getCustomButtonText(tool.ui_theme)) }}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
        
        <!-- Tips Section (Side Panel) - Only show when in input mode -->
        <div v-if="(currentView === 'input' || !generatedContent) && tool.tips && tool.tips.length" 
          class="lg:w-1/3 xl:w-1/4 hidden lg:block">
          <div class="bg-blue-50 rounded-lg shadow-sm border border-blue-100 p-4 sticky top-4">
            <h3 class="text-lg font-medium text-blue-800 mb-3 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Tips for best results
            </h3>
            <ul class="list-disc pl-5 space-y-2">
              <li v-for="(tip, index) in tool.tips" :key="index" class="text-blue-700 text-sm">
                {{ tip }}
              </li>
            </ul>
          </div>
        </div>
        
        <!-- Mobile Tips Section (shown only on small screens) -->
        <div v-if="(currentView === 'input' || !generatedContent) && tool.tips && tool.tips.length" 
          class="lg:hidden mb-6 bg-blue-50 rounded-lg p-4 border border-blue-100">
          <h3 class="text-lg font-medium text-blue-800 mb-3 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Tips for best results
          </h3>
          <ul class="list-disc pl-5 space-y-2">
            <li v-for="(tip, index) in tool.tips" :key="index" class="text-blue-700 text-sm">
              {{ tip }}
            </li>
          </ul>
        </div>
        
        <!-- === OUTPUT PANEL === -->
        <!-- Only show when content has been generated and in output mode -->
        <div v-if="generatedContent && currentView === 'output'" 
          class="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 flex-1">
          <div class="p-6">
            <div class="flex justify-between items-center mb-6">
              <h2 class="text-xl font-semibold">{{ tool.output_description || 'Generated Content' }}</h2>
              <button 
                @click="currentView = 'input'"
                class="text-blue-500 hover:text-blue-700 flex items-center text-sm font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                Edit Inputs
              </button>
            </div>
            
            <!-- Generated Content Display -->
            <div class="mb-6 p-5 bg-gray-50 rounded-md border border-gray-200">
              <div class="whitespace-pre-wrap">{{ generatedContent }}</div>
            </div>
            
            <!-- Action Buttons -->
            <div class="flex flex-wrap gap-3 justify-center">
              <button 
                @click="copyToClipboard" 
                class="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                {{ copyButtonText }}
              </button>
              
              <button 
                @click="regenerateContent" 
                class="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                :disabled="isGenerating"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Regenerate
              </button>
              
              <button 
                @click="currentView = 'input'"
                class="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Inputs
              </button>
              
              <a 
                v-if="currentGeneration && currentGeneration.chat && currentGeneration.chat.id"
                :href="`/app/chat/${currentGeneration.chat.id}`" 
                class="flex items-center justify-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                Continue in Chat
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Recent Generations Panel (Collapsible) -->
      <div v-if="tool.recent_generations && tool.recent_generations.length > 0" 
        class="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
        <div class="p-4 border-b border-gray-200 cursor-pointer" @click="showHistory = !showHistory">
          <div class="flex justify-between items-center">
            <h2 class="font-semibold text-gray-700">Your Previous Generations</h2>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 transition-transform duration-200" :class="showHistory ? 'transform rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        <div v-if="showHistory" class="divide-y divide-gray-200">
          <div v-for="generation in tool.recent_generations" :key="generation.id"
            class="p-4 hover:bg-gray-50 transition-colors duration-150">
            <div class="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
              <div>
                <div class="text-sm text-gray-500">{{ formatDate(generation.created_at) }}</div>
                <div class="text-sm font-medium mt-1">
                  <span v-if="generation.prompt_details" class="text-gray-700">{{ truncate(generation.prompt_details, 70) }}</span>
                  <span v-if="generation.input_text" class="text-gray-600 block mt-1">{{ truncate(generation.input_text, 60) }}</span>
                </div>
              </div>
              
              <div class="flex gap-3">
                <a 
                  v-if="generation.chat" 
                  :href="`/app/chat/${generation.chat.slug}`"
                  class="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  View Chat
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RestService from '../services/RestService'
import VoiceTextArea from "@/app/components/VoiceTextArea.vue";

export default {
  name: 'ContentGenerator',
  components: {VoiceTextArea},
  data() {
    return {
      model: nimbleai.model,
      service: new RestService('tools', '/'),
      tool: null,
      formData: {
        prompt_details: '',
        input: '',
        tone: 'Professional'
      },
      generatedContent: '',
      isGenerating: false,
      copyButtonText: 'Copy to clipboard',
      currentGeneration: null,
      showTips: false,
      showExample: false,
      showHistory: false,
      currentView: 'input', // Either 'input' or 'output'
      customLabels: {}
    }
  },
  computed: {
    toolsUrl() {
      return window.location.host === 'www.nimbleai.com' ? '/tools' : '/'
    }
  },
  mounted() {
    console.log('Mounted ContentGenerator, tools url: ' + this.toolsUrl)
    this.loadTool()
  },
  methods: {
    loadTool() {
      this.service.get(this.$route.params.id)
        .then(tool => {
          this.tool = tool
          this.setCustomLabels(tool)
        })
        .catch(error => {
          console.error('Error loading tool:', error)
        })
    },
    
    setCustomLabels(tool) {
      // Set custom form labels based on the tool type
      const uiTheme = tool.ui_theme || ''
      
      // Set prompt details label based on UI theme
      if (uiTheme === 'legal') {
        this.customLabels.promptDetails = 'What type of legal document do you need?'
      } else if (uiTheme === 'email') {
        this.customLabels.promptDetails = 'What kind of email do you want to create?'
      } else if (uiTheme === 'career') {
        this.customLabels.promptDetails = 'What career document do you need help with?'
      } else if (uiTheme === 'marketing') {
        this.customLabels.promptDetails = 'What marketing content do you need?'
      } else if (uiTheme === 'social') {
        this.customLabels.promptDetails = 'What social media content do you want to create?'
      } else if (uiTheme === 'content') {
        this.customLabels.promptDetails = 'What content do you need help creating?'
      } else if (uiTheme === 'technical') {
        this.customLabels.promptDetails = 'What technical content do you need to simplify?'
      } else if (uiTheme === 'business') {
        this.customLabels.promptDetails = 'What business document do you need?'
      } else if (uiTheme === 'seo') {
        this.customLabels.promptDetails = 'What type of SEO content do you need?'
      } else {
        this.customLabels.promptDetails = 'What would you like to create?'
      }
    },
    
    getCustomHeading(uiTheme) {
      if (this.tool && this.tool.prompt_label) {
        return this.tool.prompt_label;
      }
      
      const headings = {
        'legal': 'Create Legal Document',
        'email': 'Create Your Email',
        'career': 'Create Career Content',
        'social': 'Create Social Media Content',
        'marketing': 'Create Marketing Content',
        'content': 'Create Content',
        'technical': 'Simplify Technical Content',
        'business': 'Create Business Document',
        'seo': 'Create SEO Content'
      }
      return headings[uiTheme] || 'Create Your Content'
    },
    
    getCustomButtonText(uiTheme) {
      if (this.tool && this.tool.action_label) {
        return this.tool.action_label;
      }
      
      const buttonText = {
        'legal': 'Generate Legal Document',
        'email': 'Generate Email',
        'career': 'Generate Career Content',
        'social': 'Generate Social Content',
        'marketing': 'Generate Marketing Copy',
        'content': 'Generate Content',
        'technical': 'Simplify Document',
        'business': 'Generate Document',
        'seo': 'Generate SEO Content'
      }
      return buttonText[uiTheme] || 'Generate Content'
    },
    
    toggleExample() {
      this.showExample = !this.showExample
    },
    
    useExample() {
      if (this.tool.input_example) {
        this.formData.input = this.tool.input_example
      }
    },
    
    generateContent() {
      this.isGenerating = true
      
      const params = {
        prompt_details: this.formData.prompt_details,
        input: this.formData.input,
        tone: this.formData.tone
      }

      const url = `/tools/${this.$route.params.id}/generate`
      
      this.service.executePost(url, { parameters: params })
        .then(response => {
          this.generatedContent = response.result
          this.currentGeneration = {
            id: response.generation_id,
            chat: response.chat_id ? { id: response.chat_id } : null
          }
          
          // Switch to output view
          this.currentView = 'output'
          
          // Refresh the tool to get updated recent generations
          this.loadTool()
        })
        .catch(error => {
          console.error('Error generating content:', error)
          alert('There was an error generating your content. Please try again.')
        })
        .finally(() => {
          this.isGenerating = false
        })
    },
    
    regenerateContent() {
      // Just call generate again with the same parameters
      this.currentView = 'input'
      this.generateContent()
    },
    
    loadGeneration(generationId) {
      this.isGenerating = true
      
      this.service.get(`/tool_generations/${generationId}`)
        .then(generation => {
          // Fill form with saved parameters
          this.formData.prompt_details = generation.prompt_details || ''
          this.formData.input = generation.input_text || ''
          this.formData.tone = generation.tone || 'Professional'
          
          // Set generated content
          this.generatedContent = generation.generated_content
          
          // Set current generation
          this.currentGeneration = {
            id: generation.id,
            chat: generation.chat
          }
          
          // Switch to output view
          this.currentView = 'output'
        })
        .catch(error => {
          console.error('Error loading generation:', error)
          alert('Failed to load the selected generation.')
        })
        .finally(() => {
          this.isGenerating = false
        })
    },
    
    copyToClipboard() {
      if (!this.generatedContent) return
      
      navigator.clipboard.writeText(this.generatedContent)
        .then(() => {
          this.copyButtonText = 'Copied!'
          setTimeout(() => {
            this.copyButtonText = 'Copy to clipboard'
          }, 2000)
        })
        .catch(err => {
          console.error('Failed to copy text: ', err)
          alert('Failed to copy to clipboard. Please try selecting and copying the text manually.')
        })
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    truncate(str, length) {
      if (!str) return ''
      return str.length > length ? str.substring(0, length) + '...' : str
    }
  }
}
</script>

<style scoped>
/* You can add scoped styles if needed */
</style>